import React from 'react';
import { useTranslation } from 'react-i18next';
import { Order } from '@hero/schema';
import { TableCell, StatusBadgeConfigType } from '@hero/web-components';

import { OrderTableColumn } from '@hero/hooks-shared';
import {
  DestinationCell,
  StatusCell,
  ActionsCell,
  PODRefCell,
  DriverCell,
  PriceCell,
} from '.';
import { RefDisplay } from '../../../../app/Ref';
import { GovernmentWaybillRef } from '../../../Apps/GovernmentWaybillRef';
import { CellText } from '../../../../app/Table/CellText';
import { UseOrderAgainConfig } from '@hero/hooks-shared';
import { getOrderClientName, getOrderDisplayFromTo } from '@hero/core';
import { Price } from '../../../../app/Price';
import { UserIcon } from '@hero/web-icons';

export interface OrderTableCellProps {
  column: OrderTableColumn;
  order: Order;
  displayForUser: 'customer' | 'carrier';
  statusConfig?: StatusBadgeConfigType;
  hasDetailDrawer?: boolean;
  updateOrderFn?: (data: { _id: string; pod: { podRef?: string } }) => Promise<Order>;
  orderAgainConfig?: UseOrderAgainConfig;
  onOpenDrawer?: () => void;
}

/**
 * OrderTableCell - Centralized component for rendering order table cells
 *
 * Delegates complex rendering to specialized cell components:
 * - Simple columns (ref, driver, sender, receiver, payment, distance) → text/strings
 * - destinations → DestinationCell
 * - status → StatusCell
 * - actions → ActionsCell
 */
export const OrderTableCell: React.FC<OrderTableCellProps> = ({
  column,
  order,
  displayForUser,
  statusConfig,
  hasDetailDrawer = false,
  updateOrderFn,
  orderAgainConfig,
  onOpenDrawer,
}) => {
  const { key } = column;
  const { t, i18n } = useTranslation(['c-dates', 'm-order']);

  const getCellContent = (): React.ReactNode => {
    if (column.render) {
      return column.render(order, displayForUser);
    }

    switch (key) {
      case 'ref': {
        const clientName = getOrderClientName(order);
        return (
          <div className="flex flex-col items-start gap-0.5 min-w-0">
            {/* Government mark shares the ref's line: it is one glyph, and a
                row of its own would add height to every order in the table,
                including the ones with no waybill. */}
            <div className="flex min-w-0 items-center gap-1.5">
              <RefDisplay
                refMain={order.ref?.main}
                refShort={order.ref?.short}
                onClick={e => {
                  e.stopPropagation();
                  onOpenDrawer?.();
                }}
              />
              <GovernmentWaybillRef order={order} />
            </div>
            {clientName ? (
              <span
                className="inline-flex items-center gap-1 text-xs text-muted-foreground min-w-0 max-w-[180px]"
                title={clientName}>
                <UserIcon className="size-3 flex-shrink-0" strokeWidth={2} />
                <span className="truncate">{clientName}</span>
              </span>
            ) : null}
          </div>
        );
      }

      case 'driver':
        return <DriverCell order={order} className={column.className} />;

      case 'sender':
        return <CellText value={order.sender?.name} fallback={t('m-order:table.columns.not_assigned')} />;

      case 'receiver':

      case 'customer':
        return <CellText value={order.receiver?.contact?.name} fallback={t('m-order:table.columns.not_assigned')} />;

      case 'price':
        return <PriceCell order={order} />;

      case 'payment': {
        // Phase 7 (relations-v2): pricing now lives per-leg under
        // `Order.contracts[]`. We pick the leg that matches the
        // viewer's role — first leg = customer-side amount (top of
        // chain), last leg = carrier-side amount (bottom of chain).
        // When no contract has been struck yet (auto / own_fleet
        // pre-allocation), render an em-dash placeholder.
        const contracts = order.contracts ?? [];
        const amount =
          displayForUser === 'carrier'
            ? contracts[contracts.length - 1]?.amount
            : contracts[0]?.amount;
        return (
          <CellText
            variant="amount"
            value={
              amount ? (
                <Price amount={amount.total} currency={amount.currency} />
              ) : (
                t('m-order:table.columns.not_assigned')
              )
            }
          />
        );
      }

      case 'distance':
        return <CellText value={order.distance.distance.text} />;

      case 'destinations':
        return <DestinationCell order={order} />;

      case 'status':
        return (
          <StatusCell
            order={order}
            displayForUser={displayForUser}
            statusConfig={statusConfig}
            className={column.className}
          />
        );

      case 'podRef':
        return <PODRefCell order={order} updateOrderFn={updateOrderFn} />;

      case 'date': {
        if (!order.dates?.pickup?.from || !order.dates?.pickup?.to) {
          return <CellText variant="muted" value={null} />;
        }

        const translateDate = (key: string, fallback?: string): string => {
          if (key === 'date.today') return t('relative_dates.today');
          if (key === 'date.tomorrow') return t('relative_dates.tomorrow');
          return t(key, { defaultValue: fallback || key });
        };

        const dateDisplay = getOrderDisplayFromTo({
          from: order.dates.pickup.from,
          to: order.dates.pickup.to,
          translate: translateDate,
          isRTL: i18n.dir() === 'rtl',
        });

        return (
          <CellText
            variant="primary"
            value={dateDisplay.dateAutoOnly}
            desc={dateDisplay.timeOnly}
          />
        );
      }

      case 'actions':
        return (
          <ActionsCell
            order={order}
            hasDetailDrawer={hasDetailDrawer}
            orderAgainConfig={orderAgainConfig}
            displayForUser={displayForUser}
          />
        );

      default:
        return null;
    }
  };

  const content = getCellContent();

  if (content === null) {
    return null;
  }

  if (key === 'actions') {
    return <TableCell onClick={(e: React.MouseEvent) => e.stopPropagation()}>{content}</TableCell>;
  }

  if (key === 'ref') {
    return <TableCell className={column.className}>{content}</TableCell>;
  }

  if (key === 'driver' || key === 'status') {
    return content as React.ReactElement;
  }

  return <TableCell className={column.className}>{content}</TableCell>;
};
